// Beta K19 (#1317) — the Services card's copy of the after-booking messages.
// Loads once per card; `applySaved` folds a save into it, so a row reflects
// the owner's words and the new last-change line without a refetch.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchServicePaymentMessages,
  type ServicePaymentMessageEntry,
  type ServicePaymentMessagesView,
} from './service-payment-messages-api.js';

export type ServicePaymentMessagesState =
  | { status: 'loading' }
  | { status: 'not_deployed' }
  | { status: 'error' }
  | { status: 'ready'; view: ServicePaymentMessagesView };

export interface UseServicePaymentMessages {
  state: ServicePaymentMessagesState;
  reload: () => void;
  applySaved: (entry: ServicePaymentMessageEntry) => void;
}

export function useServicePaymentMessages(apiUrl: string): UseServicePaymentMessages {
  const [state, setState] = useState<ServicePaymentMessagesState>({ status: 'loading' });
  const seq = useRef(0);

  const reload = useCallback(() => {
    const mine = ++seq.current;
    setState({ status: 'loading' });
    void fetchServicePaymentMessages(apiUrl).then((r) => {
      if (mine !== seq.current) return;
      if (r.kind === 'ok') setState({ status: 'ready', view: r.view });
      else setState({ status: r.kind === 'not_deployed' ? 'not_deployed' : 'error' });
    });
  }, [apiUrl]);

  useEffect(() => {
    reload();
    return () => {
      seq.current++;
    };
  }, [reload]);

  const applySaved = useCallback((entry: ServicePaymentMessageEntry) => {
    setState((prev) =>
      prev.status === 'ready'
        ? {
            status: 'ready',
            view: {
              ...prev.view,
              services: prev.view.services.map((s) => (s.serviceTypeId === entry.serviceTypeId ? entry : s)),
            },
          }
        : prev,
    );
  }, []);

  return { state, reload, applySaved };
}
